import { Icon } from '@uag/react-core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlacementUpdate } from 'app/modules/wallforming/components/content-container/types/placementUpdate';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

const UPDATE_DISPLAY_MS = 3_000;
const UPDATE_FADE_MS = 500;

export const PlacementUpdateInfo = () => {
    const { t } = useTranslation();
    const [placementUpdate, setPlacementUpdate] = useState<PlacementUpdate | null>(null);
    const [updateFading, setUpdateFading] = useState<boolean>(false);

    const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const placementUpdateRef = useRef<PlacementUpdate | null>(null);

    useEffect(() => {
        const unsubscribe = usePlacementStore.subscribe(
            (state) => state.placementUpdate,
            (newUpdate) => {
                if (!newUpdate) {
                    return;
                }

                /* 
                    batch hub connection updates of similar event types":
                    e.g. "6 deleted" fires 3× -> "18 deleted" will be shown instead of three identical "6 deleted" notifications
                */

                let update;
                if (placementUpdateRef.current && placementUpdateRef.current.event === newUpdate.event) {
                    // Only add new articleinstanceIds to the existing update to prevent increasing counts for the same articles
                    const existingIds = placementUpdateRef.current.articleInstanceIds;
                    const newIds = [...newUpdate.articleInstanceIds].filter((id) => !existingIds.has(id));

                    update = {
                        ...placementUpdateRef.current,
                        articleInstanceIds: new Set([...placementUpdateRef.current.articleInstanceIds, ...newIds])
                    };
                } else {
                    update = newUpdate;
                }

                placementUpdateRef.current = update;

                clearTimeout(fadeTimerRef.current);
                clearTimeout(cleanupTimerRef.current);

                setUpdateFading(false);
                setPlacementUpdate(update);

                fadeTimerRef.current = setTimeout(() => {
                    setUpdateFading(true);
                }, UPDATE_DISPLAY_MS - UPDATE_FADE_MS);

                cleanupTimerRef.current = setTimeout(() => {
                    placementUpdateRef.current = null;
                    setPlacementUpdate(null);
                    setUpdateFading(false);
                }, UPDATE_DISPLAY_MS);
            }
        );

        return () => {
            clearTimeout(fadeTimerRef.current);
            clearTimeout(cleanupTimerRef.current);
            placementUpdateRef.current = null;
            unsubscribe();
        };
    }, []);

    return (
        <>
            {placementUpdate && (
                <span
                    className="flex items-center gap-1 transition-opacity text-xs leading-none min-w-0 max-w-full mr-3"
                    style={{
                        opacity: updateFading ? 0 : 1,
                        transitionDuration: `${UPDATE_FADE_MS}ms`
                    }}
                >
                    <Icon className="icon-medium" color={PRIMARY_COLOR_ACCENT_HEX} icon="notifications_active" />
                    <span className="truncate">
                        {t(placementUpdate.event, {
                            count: placementUpdate.articleInstanceIds.size
                        })}
                    </span>
                </span>
            )}
        </>
    );
};
